// Administrador Master → órdenes de compra y campañas pendientes (ADR-0047). Approving an order reserves
// credit; authorizing a campaign allocates budget from its order and releases the campaign's commercial
// hold. A rejection always needs a reason. The decision happens here, never by replying to an email.
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type SyntheticEvent } from "react";
import { toast } from "sonner";
import { ApiProblem } from "@/api/errors";
import { API_ORIGIN } from "@/api/config";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Dialog, DialogContent } from "@/ui/dialog";
import { Field } from "@/ui/field";
import { Input } from "@/ui/input";
import { EmptyState, PageHeader, Table, Td, Th } from "@/ui/layout";
import { PageError, PageLoading, useTitle } from "../common/page";
import {
  AUTH_STATUS,
  COMPANY_STATUS,
  formField,
  ORG_TYPE_LABELS,
  PO_STATUS,
  ruleMessage,
  usd,
} from "../commercial/labels";
import { adminPost, useAdmin, useAdminMutation } from "./admin-api";

const select =
  "h-8 rounded-md border border-line-strong bg-surface px-2 text-sm text-fg focus-visible:outline-2 focus-visible:outline-brand";
const usdInput = (micros: number) => (micros / 1_000_000).toFixed(2);
const problemText = (e: unknown) => (e instanceof ApiProblem ? ruleMessage(e) : "No se pudo completar la acción.");

interface Decision {
  kind: "po" | "auth";
  id: string;
  title: string;
  maxMicros: number;
}

function DecisionDialog({ decision, onClose }: { decision: Decision | null; onClose: () => void }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const shown = amount || (decision ? usdInput(decision.maxMicros) : "");
  const m = useAdminMutation((input: { approve: boolean }) => {
    if (!decision) return Promise.resolve();
    const body = {
      decision: input.approve ? ("APPROVE" as const) : ("REJECT" as const),
      approvedUsd: input.approve ? shown : null,
      reason: reason.trim() || null,
    };
    return decision.kind === "po" ? adminPost.poDecision(decision.id, body) : adminPost.authDecision(decision.id, body);
  });
  const run = (approve: boolean) => {
    m.mutate(
      { approve },
      {
        onSuccess: () => {
          toast.success(
            approve ? "Aprobado. Notificamos a la empresa." : "Rechazado. Notificamos el motivo a la empresa."
          );
          setAmount("");
          setReason("");
          onClose();
        },
        onError: (e) => toast.error(problemText(e)),
      }
    );
  };
  return (
    <Dialog
      open={decision !== null}
      onOpenChange={(open) => {
        if (!open) {
          setAmount("");
          setReason("");
          onClose();
        }
      }}
    >
      {decision ? (
        <DialogContent title={decision.title} description="La decisión queda registrada con tu usuario, fecha y hora.">
          <div className="grid gap-3">
            <Field label="Monto aprobado (USD)" hint={`Hasta ${usd(decision.maxMicros)}.`}>
              <Input
                value={shown}
                onChange={(e) => {
                  setAmount(e.target.value);
                }}
                inputMode="decimal"
              />
            </Field>
            <Field label="Motivo u observación" hint="Obligatorio para rechazar; la empresa lo recibe.">
              <Input
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                }}
                maxLength={500}
              />
            </Field>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="danger"
                disabled={m.isPending || reason.trim().length < 3}
                onClick={() => {
                  run(false);
                }}
              >
                Rechazar
              </Button>
              <Button
                variant="primary"
                disabled={m.isPending}
                onClick={() => {
                  run(true);
                }}
              >
                Aprobar
              </Button>
            </div>
          </div>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}

interface PoRow {
  id: string;
  organizationId: string;
  company: string | null;
  internalRef: string;
  poNumber: string;
  campaignName: string;
  amountMicros: number;
  approvedAmountMicros: number | null;
  unallocatedMicros: number;
  startsOn: string;
  endsOn: string;
  responsibleName: string;
  status: string;
  decisionReason: string | null;
  isTest: boolean;
  closedAt?: string | null;
}

/** Closes an approved order (CA-O1): releases its unconsumed reservation; it funds no new campaign. */
function ClosePurchaseOrder({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const m = useAdminMutation((reason: string) => adminPost.closePo(id, reason));
  if (!open)
    return (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setOpen(true);
        }}
      >
        Cerrar orden
      </Button>
    );
  return (
    <form
      className="grid gap-1"
      onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();
        m.mutate(formField(new FormData(event.currentTarget), "reason").trim(), {
          onSuccess: () => {
            toast.success("Orden cerrada: se liberó la reserva no consumida.");
            setOpen(false);
          },
          onError: (e) => toast.error(problemText(e)),
        });
      }}
    >
      <Input name="reason" aria-label="Motivo del cierre" placeholder="Motivo" minLength={3} maxLength={500} required />
      <Button type="submit" size="sm" variant="secondary" disabled={m.isPending}>
        Confirmar cierre
      </Button>
    </form>
  );
}

export function AdminPurchaseOrders() {
  useTitle("Órdenes de compra");
  const search: { status?: string } = useSearch({ strict: false });
  const navigate = useNavigate();
  const status = search.status ?? "PENDING_REVIEW";
  const { data, isLoading, error } = useAdmin<{ items: PoRow[] }>("/admin/purchase-orders", {
    status: status === "ALL" ? undefined : status,
  });
  const [decision, setDecision] = useState<Decision | null>(null);
  return (
    <div className="grid gap-6">
      <PageHeader
        title="Órdenes de compra"
        description="Aprobar una orden reserva su monto en el crédito de la empresa."
      />
      <select
        aria-label="Estado"
        className={`${select} w-fit`}
        value={status}
        onChange={(e) => void navigate({ to: "/admin/purchase-orders", search: { status: e.target.value } })}
      >
        {Object.entries(PO_STATUS).map(([k, v]) => (
          <option key={k} value={k}>
            {v.label}
          </option>
        ))}
        <option value="ALL">Todas</option>
      </select>
      {isLoading ? (
        <PageLoading />
      ) : error ? (
        <PageError error={error} />
      ) : !data?.items.length ? (
        <EmptyState title="Nada pendiente" description="No hay órdenes con este estado." />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th>Orden</Th>
                <Th>Empresa</Th>
                <Th>Campaña</Th>
                <Th className="text-right">Inversión</Th>
                <Th className="text-right">Sin asignar</Th>
                <Th>Período</Th>
                <Th>Estado</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((po) => (
                <tr key={po.id}>
                  <Td>
                    <div className="font-mono text-xs">{po.internalRef}</div>
                    <div>{po.poNumber}</div>
                    <a
                      className="text-xs text-brand underline"
                      href={`${API_ORIGIN ?? ""}/admin/purchase-orders/${po.id}/document`}
                    >
                      PDF
                    </a>
                  </Td>
                  <Td>
                    <Link className="text-brand hover:underline" to={`/admin/companies/${po.organizationId}`}>
                      {po.company ?? "—"}
                    </Link>
                    {po.isTest ? <Badge className="ml-2">Prueba</Badge> : null}
                  </Td>
                  <Td>
                    {po.campaignName}
                    <div className="text-xs text-muted">{po.responsibleName}</div>
                  </Td>
                  <Td className="text-right tabular-nums">{usd(po.amountMicros)}</Td>
                  <Td className="text-right tabular-nums">
                    {po.status === "APPROVED" ? usd(po.unallocatedMicros) : "—"}
                  </Td>
                  <Td className="whitespace-nowrap text-xs">
                    {po.startsOn} → {po.endsOn}
                  </Td>
                  <Td>
                    <Badge tone={PO_STATUS[po.status]?.tone ?? "neutral"}>{PO_STATUS[po.status]?.label}</Badge>
                    {po.decisionReason ? <div className="mt-1 text-xs text-muted">{po.decisionReason}</div> : null}
                  </Td>
                  <Td>
                    {po.status === "PENDING_REVIEW" ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setDecision({
                            kind: "po",
                            id: po.id,
                            title: `Orden ${po.internalRef}`,
                            maxMicros: po.amountMicros,
                          });
                        }}
                      >
                        Revisar
                      </Button>
                    ) : po.status === "APPROVED" && !po.closedAt ? (
                      <ClosePurchaseOrder id={po.id} />
                    ) : po.closedAt ? (
                      <span className="text-xs text-muted">Cerrada</span>
                    ) : null}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      <DecisionDialog
        decision={decision}
        onClose={() => {
          setDecision(null);
        }}
      />
    </div>
  );
}

interface AuthRow {
  id: string;
  organizationId: string;
  company: string | null;
  orgType: string | null;
  companyStatus: string | null;
  campaign: string;
  client: string;
  purchaseOrderId: string;
  purchaseOrderRef: string;
  poNumber: string;
  requestedBudgetMicros: number;
  approvedBudgetMicros: number | null;
  startsOn: string;
  endsOn: string;
  responsibleName: string;
  status: string;
  decisionReason: string | null;
  isTest: boolean;
  purchaseOrderUnallocatedMicros: number;
  creditAvailableMicros: number;
  billingAlerts: number;
}

export function AdminAuthorizations() {
  useTitle("Campañas pendientes");
  const search: { status?: string } = useSearch({ strict: false });
  const navigate = useNavigate();
  const status = search.status ?? "SUBMITTED";
  const { data, isLoading, error } = useAdmin<{ items: AuthRow[] }>("/admin/authorizations", {
    status: status === "ALL" ? undefined : status,
  });
  const [decision, setDecision] = useState<Decision | null>(null);
  const check = useAdminMutation((id: string) => adminPost.budgetCheck(id));
  return (
    <div className="grid gap-6">
      <PageHeader
        title="Campañas pendientes"
        description="La autorización comercial no reemplaza la validación técnica: la campaña se distribuye cuando también tiene un creativo publicado."
      />
      <select
        aria-label="Estado"
        className={`${select} w-fit`}
        value={status}
        onChange={(e) => void navigate({ to: "/admin/authorizations", search: { status: e.target.value } })}
      >
        {Object.entries(AUTH_STATUS).map(([k, v]) => (
          <option key={k} value={k}>
            {v.label}
          </option>
        ))}
        <option value="ALL">Todas</option>
      </select>
      {isLoading ? (
        <PageLoading />
      ) : error ? (
        <PageError error={error} />
      ) : !data?.items.length ? (
        <EmptyState title="Nada pendiente" description="No hay solicitudes con este estado." />
      ) : (
        <div className="grid gap-3">
          {data.items.map((a) => (
            <div key={a.id} className="grid gap-3 rounded-md border border-line bg-surface p-4 md:grid-cols-[1fr_auto]">
              <div className="grid gap-1 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{a.campaign}</span>
                  <Badge tone={AUTH_STATUS[a.status]?.tone ?? "neutral"}>{AUTH_STATUS[a.status]?.label}</Badge>
                  {a.isTest ? <Badge>Prueba</Badge> : null}
                  {a.billingAlerts > 0 ? <Badge tone="danger">Alerta de facturación</Badge> : null}
                </div>
                <div>
                  <Link className="text-brand hover:underline" to={`/admin/companies/${a.organizationId}`}>
                    {a.company ?? "—"}
                  </Link>{" "}
                  · {a.orgType ? ORG_TYPE_LABELS[a.orgType] : ""} · Cliente: {a.client} ·{" "}
                  <Badge tone={COMPANY_STATUS[a.companyStatus ?? "NO_PROFILE"]?.tone ?? "neutral"}>
                    {COMPANY_STATUS[a.companyStatus ?? "NO_PROFILE"]?.label}
                  </Badge>
                </div>
                <div className="text-muted">
                  Orden {a.purchaseOrderRef} ({a.poNumber}) ·{" "}
                  <a
                    className="text-brand underline"
                    href={`${API_ORIGIN ?? ""}/admin/purchase-orders/${a.purchaseOrderId}/document`}
                  >
                    PDF
                  </a>{" "}
                  · {a.startsOn} → {a.endsOn} · Responsable: {a.responsibleName}
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1 sm:grid-cols-4">
                  <Mini label="Presupuesto solicitado" value={usd(a.requestedBudgetMicros)} />
                  <Mini label="Aprobado" value={a.approvedBudgetMicros ? usd(a.approvedBudgetMicros) : "—"} />
                  <Mini label="Saldo sin asignar de la orden" value={usd(a.purchaseOrderUnallocatedMicros)} />
                  <Mini label="Crédito disponible" value={usd(a.creditAvailableMicros)} />
                </div>
                {a.decisionReason ? <div className="text-xs text-muted">Motivo: {a.decisionReason}</div> : null}
              </div>
              <div className="flex flex-wrap items-start gap-2">
                {a.status === "SUBMITTED" ? (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setDecision({
                        kind: "auth",
                        id: a.id,
                        title: `Autorizar ${a.campaign}`,
                        maxMicros: a.requestedBudgetMicros,
                      });
                    }}
                  >
                    Revisar
                  </Button>
                ) : null}
                {a.status === "APPROVED" ? (
                  <Button
                    variant="secondary"
                    disabled={check.isPending}
                    onClick={() => {
                      check.mutate(a.id, {
                        onSuccess: (r) => {
                          const result = r as { outcome: string; consumedMicros: number };
                          toast.success(
                            result.outcome === "BUDGET_AVAILABLE"
                              ? `Consumido ${usd(result.consumedMicros)}: aún hay presupuesto.`
                              : `Consumido ${usd(result.consumedMicros)}: la campaña quedó detenida.`
                          );
                        },
                        onError: (e) => toast.error(problemText(e)),
                      });
                    }}
                  >
                    Revisar consumo
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
      <DecisionDialog
        decision={decision}
        onClose={() => {
          setDecision(null);
        }}
      />
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-sm bg-sunken px-2 py-1">
      <div className="text-[11px] text-muted">{label}</div>
      <div className="font-medium tabular-nums">{value}</div>
    </div>
  );
}
